import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useStepsQuery } from './useStepsQuery';
import { useStepsSearch } from './useStepsSearch';
import { useStepsTableColumns } from './useStepsTableColumns';
import { Step } from '../types';

// DEFAULT: position ASCENDING — and this is the one table on the platform where
// "newest first" would be wrong.
//
// Steps are a SEQUENCE, not an unordered list. `position` is the sequence: it is
// `position: number` (required, non-null on the type), immutable in the sense
// that it only changes when an operator deliberately reorders the phase, and the
// backend serves this list from `record-type-position-index`, which already
// returns each page position-ascending. So this default satisfies all three of
// the checklist's requirements at once — immutable, non-null, and AGREEING with
// the API's own order, so the first paint does not reshuffle what the server
// returned. Defaulting to `created` desc would render the steps of a playbook
// phase in an order that means nothing to the operator reading them.
//
// Sorting the other columns is still offered, because an operator scanning for
// a target service genuinely wants it. The checklist requires sortable columns
// and does not exempt sequences; what it forbids is a default that misrepresents
// the data, which position-ascending is the cure for rather than the cause.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Step> = {
    columnAccessor: 'position',
    direction: 'asc',
};

export const useSteps = (filters: Record<string, string> = {}) => {
    const query = useStepsQuery(filters);
    const { keyword } = useStepsSearch();
    const { columns } = useStepsTableColumns();

    // Type guard, not a bare flatMap: a page can be undefined while a fetch is
    // in flight, and `page.items` on it throws.
    const items: Step[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen. For a SEQUENCE this is worth being
    // precise about: the loaded window is a position-ascending PREFIX of the
    // phase's steps, so what the operator sees is the first N steps in order —
    // not an arbitrary sample reordered to look sequential. Loading more
    // extends the sequence rather than rearranging it.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.name?.toLowerCase().includes(needle) ||
        item.displayName?.toLowerCase().includes(needle) ||
        item.description?.toLowerCase().includes(needle)
    );

    // Sort state is component-level (inside useTableSort), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike the keyword, which is shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<Step>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
        columns,
    };
};
